import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import Tabs from '@/common/components/Tabs'
import useCompany from '@/common/hooks/queries/useCompany'
import { cn } from '@/common/utils/cn'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { formatCurrency } from '@/common/utils/formatters'
import { GovernmentAllocationDropdown } from '@/features/privateCompanies/components/GovernmentAllocationDropdown'
import { useCompanyBranch } from '@/features/privateCompanies/hooks/useCompanyBranch'
import { useCompanyGovernmentAllocation } from '@/features/privateCompanies/hooks/useCompanyGovernmentAllocation'
import { GovernmentAllocationDistribution } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/components/GovernmentAllocationDistribution'
import { GovernmentAllocationDocuments } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/components/GovernmentAllocationDocuments'
import { GovernmentAllocationInfoItem } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/components/GovernmentAllocationInfoItem'
import { companyGovernmentAllocationStatusStrategy } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Show/constants/governmentAllocationStatus.constant'
import { isAfter, isWithinInterval } from 'date-fns'
import { ChevronsRightIcon, FileCheckIcon, FileClockIcon, OctagonAlertIcon } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import ShowGovernmentAllocationLoadingState from './components/ShowGovernmentAllocationLoadingState'
import GovernmentAllocationBills from './components/GovernmentAllocationBills'
import { GovernmentAllocationDistributionsLimits } from './components/GovernmentAllocationDistributionsLimits'
import { GovernmentAllocationSummary } from './components/GovernmentAllocationSummary'

const Show = () => {
  const { companyId, id } = useParams()

  const { data: company, isPending: companyIsPending } = useCompany({ id: companyId || '' })

  const {
    data: companyGovernmentAllocation,
    isPending: companyGovernmentAllocationIsPending,
    error: companyGovernmentAllocationError = {} as any,
    refetch: refetchCompanyGovernmentAllocation,
  } = useCompanyGovernmentAllocation(id || '')

  const { data: companyBranch, isPending: companyBranchIsPending } = useCompanyBranch(
    companyGovernmentAllocation?.branch_id || '',
    !!companyGovernmentAllocation?.branch_id,
  )

  const isInPeriod = useMemo(() => {
    return companyGovernmentAllocation
      ? isWithinInterval(new Date(), {
        start: new Date(companyGovernmentAllocation?.applicable_start_date),
        end: new Date(companyGovernmentAllocation?.applicable_end_date),
      })
      : false
  }, [companyGovernmentAllocation?.applicable_start_date, companyGovernmentAllocation?.applicable_end_date])

  const isExpired = useMemo(() => {
    return companyGovernmentAllocation
      ? isAfter(new Date(), new Date(companyGovernmentAllocation?.applicable_end_date))
      : false
  }, [companyGovernmentAllocation?.applicable_end_date])

  if (
    companyGovernmentAllocationIsPending ||
    companyIsPending ||
    (companyBranchIsPending && companyGovernmentAllocation?.branch_id)
  ) {
    return <ShowGovernmentAllocationLoadingState />
  }

  return (
    <ErrorMessageWrapper
      className="max-h-[calc(100vh-64px)]"
      statusCode={companyGovernmentAllocationError?.status}
      refetchFunction={refetchCompanyGovernmentAllocation}
      notFoundFallback={`/companies/${companyId}/government-allocation`}
      notFoundButtonText={
        company?.current_contract?.payment_modality === 'pre_paid'
          ? 'Ir para Empenhos'
          : 'Ir para Cotas'
      }
    >
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-4 items-center">
          <GoBackButton
            fallback={`/companies/${companyId}/government-allocation`}
            searchLastRouteFromFallback
          />

          <div className="flex items-center gap-1">
            <p className="text-white-400">
              {company?.general_information?.fantasy_name + ` (${companyBranch?.display_name})`}
            </p>

            <ChevronsRightIcon className="text-white-400" />

            <p className="text-white-800">{companyGovernmentAllocation?.main_entry?.office_number}</p>
          </div>
        </div>
      </div>

      {companyGovernmentAllocation && (
        <Box className="flex flex-col gap-6 h-[calc(100vh-128px)] overflow-y-auto">
          <div className="flex justify-between">
            <div className="flex items-end gap-2">
              <p className="text-lg font-bold leading-5">Empenho</p>
              <Link
                to={`/companies/${companyId}/government-allocations/${companyGovernmentAllocation.main_entry.id}/edit`}
              >
                <Button variant="link">Ver contrato</Button>
              </Link>
              <div className="flex items-center gap-2">
                {isExpired ? (
                  <FileClockIcon strokeWidth={1} className="size-6 text-danger" />
                ) : (
                  <FileCheckIcon strokeWidth={1} className="size-6 text-success" />
                )}
                <p className={cn('text-xs font-bold leading-3', isExpired ? 'text-danger-soft' : 'text-valid')}>
                  {isExpired ? 'Expirado' : 'Vigente'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <GovernmentAllocationDropdown
                companyId={companyId || ''}
                defaultOpen={Number(companyGovernmentAllocation.approved_value) === 0}
                companyGovernmentAllocation={companyGovernmentAllocation}
              />
            </div>
          </div>
          <div className="flex items-start gap-6">
            <GovernmentAllocationInfoItem
              label="Status"
              value={companyGovernmentAllocationStatusStrategy[
                companyGovernmentAllocation.status as keyof typeof companyGovernmentAllocationStatusStrategy
              ]}
            />

            <GovernmentAllocationInfoItem
              label="Nº Empenho"
              value={companyGovernmentAllocation.main_entry.office_number}
            />

            <GovernmentAllocationInfoItem
              label="Período de competência"
              value={`${dateTransformer(companyGovernmentAllocation.applicable_start_date)} à ${dateTransformer(
                companyGovernmentAllocation.applicable_end_date,
              )}`}
            />

            <GovernmentAllocationInfoItem
              label="Tipo de controle de empenho"
              value={companyGovernmentAllocation.controlled_by === 'quantity' ? 'Por quantidade' : 'Por valor em R$'}
            />
          </div>

          {companyGovernmentAllocation.controlled_by === 'currency' && (
            <div className="flex items-start gap-6">
              <GovernmentAllocationInfoItem
                label="Valor total empenhado"
                value={formatCurrency(Number(companyGovernmentAllocation.total_value))}
              />

              <GovernmentAllocationInfoItem
                label="Valor total aprovado"
                value={formatCurrency(Number(companyGovernmentAllocation.approved_value))}
                dataTestid="approved_value"
              />

              <GovernmentAllocationInfoItem
                label="Restante a aprovar"
                value={formatCurrency(
                  Number(companyGovernmentAllocation.total_value) - Number(companyGovernmentAllocation.approved_value),
                )}
              />

              <GovernmentAllocationInfoItem
                label="Total distribuido"
                value={formatCurrency(Number(companyGovernmentAllocation.distributed_total))}
              />

              <GovernmentAllocationInfoItem
                label="Restante a distribuir"
                value={formatCurrency(Number(companyGovernmentAllocation.available_to_distribution))}
              />

              <GovernmentAllocationInfoItem
                label="Valor total consumido"
                value={formatCurrency(Number(companyGovernmentAllocation.consumed_total))}
                className="text-yellow-400"
              />

              <div className="flex items-start gap-2">
                {isExpired && <OctagonAlertIcon strokeWidth={2} className="size-5" color="#FBBCB6" />}

                <GovernmentAllocationInfoItem
                  label={isExpired ? 'Valor total bloqueado' : 'Valor total disponível'}
                  value={formatCurrency(Number(companyGovernmentAllocation.remaining_total))}
                  className={cn(isExpired ? 'text-danger-soft' : 'text-valid')}
                />
              </div>
            </div>
          )}

          <Tabs
            tabsListClassName="w-full bg-transparent rounded-none"
            tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
            tabsContentClassName="mt-6"
            tabsList={[
              {
                label: 'Resumo de distribuições',
                value: '',
                body: <GovernmentAllocationSummary companyGovernmentAllocation={companyGovernmentAllocation} />,
              },
              {
                label: 'Documentos de empenho',
                value: 'documents',
                body: (
                  <GovernmentAllocationDocuments
                    companyGovernmentAllocation={companyGovernmentAllocation}
                    showReinforcementButton={!isExpired}
                    disableReinforcement={!isInPeriod}
                  />
                ),
              },
              {
                label: 'Divisão de crédito do empenho',
                value: 'distribution',
                body: (
                  <GovernmentAllocationDistribution controlledBy={companyGovernmentAllocation.controlled_by} />
                ),
              },
              {
                label: 'Limites de distribuição do empenho',
                value: 'distribution-limits',
                operationKlassToCheckPermission: 'Admin::Billing::GovernmentAllocations::DistributionsLimits',
                body: <GovernmentAllocationDistributionsLimits controlledBy={companyGovernmentAllocation.controlled_by} />,
              },
              {
                label: 'Faturas do empenho',
                value: 'bills',
                operationKlassToCheckPermission: 'Admin::Billing::GovernmentAllocations::BillsFilter',
                body: <GovernmentAllocationBills companyGovernmentAllocation={companyGovernmentAllocation} />,
              },
            ]}
          />
        </Box>
      )}
    </ErrorMessageWrapper>
  )
}

export default Show
